/* eslint-disable react/prop-types */
import React from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    initializeWhatsapp,
    saveWhatsappChatMetaData,
    resetWhatsappChat,
} from 'src/redux/slices/whatsappV2';
import ChatPage from 'src/pages/whatsapp/ChatPage';

/**
 * FollowUpWhatsappChat - Wrapper component to embed the new WhatsApp ChatPage
 * within the FollowUp screen's OpenContactLeadPopup.
 *
 * This component handles:
 * 1. WhatsApp initialization (if not already done)
 * 2. Setting up chat metadata for the specific lead
 * 3. Rendering the ChatPage in embedded mode (no navigation, simplified header)
 *
 * Checklist WhatsApp template prefill — two shapes, both threaded to ChatPage:
 *   • `templateId` (legacy): an engagement-template id; ChatPage fetches its
 *     body and seeds the free composer (open-window only).
 *   • `prefillWaTemplateName`: the exact Meta template NAME resolved via
 *     /checklist-whatsapp-mappings/resolve/ (→ whatsapp_template_name). ChatPage
 *     matches it in the loaded template list, previews it as a draft bubble, and
 *     sends it via the approved-template path — works open OR closed window.
 *   The parents (FollowUpV2 sidebar / ChecklistItemRow slideout) resolve the
 *   mapping when a LEAD_NEXT_ACTION or WHATSAPP_SENT checklist item is selected.
 */
const FollowUpWhatsappChat = ({ user, followLead, onMessageSent, onMessageConfirmed, templateId, prefillWaTemplateId, prefillWaTemplateName, prefillWaItemId, practiceId }) => {

    const dispatch = useDispatch();
    const [isInitializing, setIsInitializing] = React.useState(true);
    const [initError, setInitError] = React.useState(null);

    const {
        isLoading,
        currentBusinessId,
        currentWhatsappPhoneId,
        whatsAppChatMetaData,
        whatsappNotConfigured,
        whatsappInitError,
    } = useSelector(
        (state) => ({
            isLoading: state?.whatsappV2?.isLoading,
            currentBusinessId: state?.whatsappV2?.currentBusinessId,
            currentWhatsappPhoneId: state?.whatsappV2?.currentWhatsappPhoneId,
            whatsAppChatMetaData: state?.whatsappV2?.whatsAppChatMetaData,
            whatsappNotConfigured: state?.whatsappV2?.whatsappNotConfigured,
            whatsappInitError: state?.whatsappV2?.whatsappInitError,
        }),
        shallowEqual
    );

    // Get lead data from either user or followLead prop
    const leadData = followLead || user;
    const phoneNumber = leadData?.lead_contact?.phone || leadData?.phone || leadData?.phoneNumber;
    const leadId = leadData?.id || leadData?.lead_id;
    const leadName = `${leadData?.first_name || leadData?.firstName || ''} ${leadData?.last_name || leadData?.lastName || ''}`.trim();

    // Check if WhatsApp is initialized
    const isWhatsappInitialized = !isLoading && currentBusinessId !== -1 && currentWhatsappPhoneId !== -1;

    // Initialize WhatsApp if needed
    // Retry-storm fix (2026-06-08): also gate on `whatsappNotConfigured` and
    // `whatsappInitError`. After a definitive "no WABA" response the slice keeps
    // `currentBusinessId === -1` AND flips `isLoading` back to false — without
    // these flags the effect re-fires forever, hammering /whatsapp-business-accounts/
    // with 100+ requests per second on no-WABA practices. The manual `handleRetry`
    // path still works because the thunk's first action (`startInitializeWhatsapp`)
    // resets the slice to initial state, clearing both flags.
    React.useEffect(() => {
        const initWhatsapp = async () => {
            if (currentBusinessId === -1 && !isLoading && !whatsappNotConfigured && !whatsappInitError) {
                setIsInitializing(true);
                setInitError(null);
                try {
                    await dispatch(initializeWhatsapp());
                } catch (error) {
                    console.error('Failed to initialize WhatsApp:', error);
                    setInitError('Failed to initialize WhatsApp. Please try again.');
                }
            }
        };

        initWhatsapp();
    }, [dispatch, currentBusinessId, isLoading, whatsappNotConfigured, whatsappInitError]);

    // Set up chat metadata once initialized
    React.useEffect(() => {
        if (isWhatsappInitialized && leadId && phoneNumber) {
            setIsInitializing(false);

            // Only set metadata if it's for a different lead or not set
            const currentLeadId = whatsAppChatMetaData?.leadId;
            if (currentLeadId !== leadId) {
                dispatch(saveWhatsappChatMetaData({
                    leadId,
                    phoneNumber: phoneNumber?.trim().replaceAll(' ', '').replaceAll('+', ''),
                    name: leadName || phoneNumber,
                    avatar: null,
                    // Don't set thread - let fetchWhatsappChatAndOtherMetaData find it
                }));
            }
        } else if (isWhatsappInitialized && !phoneNumber) {
            setIsInitializing(false);
            setInitError('No phone number available for this lead.');
        } else if (isWhatsappInitialized && !leadId) {
            setIsInitializing(false);
            setInitError('No lead selected — please provide a lead context to start a WhatsApp conversation.');
        } else if (!isLoading && whatsappNotConfigured) {
            // Init COMPLETED and reported no WABA for this practice.
            // (Bug fix 2026-06-08): previously this branch fired on `currentBusinessId === -1`
            // alone, which is ALSO the pre-init default value. On first mount the second effect
            // ran with a stale `isLoading=false` closure before the first effect's
            // `startInitializeWhatsapp` action propagated, so users saw a spurious
            // "WhatsApp is not configured" error even though init was still in flight.
            // The slice exposes `whatsappNotConfigured` exactly for this disambiguation.
            setIsInitializing(false);
            setInitError('WhatsApp is not configured for this practice.');
        } else if (!isLoading && whatsappInitError) {
            // Init COMPLETED with a transient failure (5xx, network, timeout). Surface a retryable error
            // — do NOT call this "not configured", which would be a lie about practice setup.
            setIsInitializing(false);
            setInitError('Failed to initialize WhatsApp. Please try again.');
        }
    }, [isWhatsappInitialized, leadId, phoneNumber, leadName, dispatch, whatsAppChatMetaData?.leadId, isLoading, whatsappNotConfigured, whatsappInitError]);

    // Cleanup on unmount
    React.useEffect(() => () => {
        dispatch(resetWhatsappChat());
    }, [dispatch]);

    // Handle retry
    const handleRetry = () => {
        setInitError(null);
        setIsInitializing(true);
        dispatch(initializeWhatsapp());
    };

    // Loading state
    if (isInitializing || isLoading) {
        return (
            <Box sx={styles.loadingContainer}>
                <CircularProgress size={40} sx={{ color: '#00A76F' }} />
                <Typography variant="body2" sx={{ color: '#637381', mt: 2 }}>
                    Loading WhatsApp...
                </Typography>
            </Box>
        );
    }

    // Error state
    if (initError) {
        return (
            <Box sx={styles.errorContainer}>
                <Icon icon="mdi:alert-circle-outline" width={48} height={48} style={{ color: '#FF5630' }} />
                <Typography variant="body1" sx={{ color: '#637381', mt: 2, textAlign: 'center' }}>
                    {initError}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={handleRetry}
                    sx={{ mt: 2 }}
                    startIcon={<Icon icon="mdi:refresh" />}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    // No phone number
    if (!phoneNumber) {
        return (
            <Box sx={styles.errorContainer}>
                <Icon icon="mdi:phone-off" width={48} height={48} style={{ color: '#919EAB' }} />
                <Typography variant="body1" sx={{ color: '#637381', mt: 2, textAlign: 'center' }}>
                    No phone number available for this lead.
                </Typography>
                <Typography variant="body2" sx={{ color: '#919EAB', mt: 1, textAlign: 'center' }}>
                    Add a phone number to start a WhatsApp conversation.
                </Typography>
            </Box>
        );
    }

    // Render ChatPage in embedded mode
    return (
        <Box sx={styles.chatContainer}>
            <ChatPage
                embedded
                hideResolve
                onToggleDetails={() => {}}
                detailsOpen={false}
                onMessageSent={onMessageSent}
                onMessageConfirmed={onMessageConfirmed}
                prefillTemplateId={templateId}
                prefillWaTemplateId={prefillWaTemplateId}
                prefillWaTemplateName={prefillWaTemplateName}
                prefillWaItemId={prefillWaItemId}
                prefillPracticeId={practiceId}
            />
        </Box>
    );
};

const styles = {
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '400px',
        backgroundColor: '#F4F6F8',
        borderRadius: '12px',
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '400px',
        backgroundColor: '#F4F6F8',
        borderRadius: '12px',
        padding: '24px',
    },
    chatContainer: {
        height: '100%',
        minHeight: '500px',
        maxWidth: '100%',
        backgroundColor: '#fff',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(145, 158, 171, 0.12)',
        display: 'flex',
        flexDirection: 'column',
    },
};

export default FollowUpWhatsappChat;
